// Shared legal-next-keyword model.
//
// `getLegalNextKeywords(engine, slot, opts)` returns the ordered list of
// grammatically-legal continuation keywords at a recognized cursor
// sub-position (see `nextKeywordSlot.ts` for the classifier that names the
// slot). The array order IS the popularity order — index 0 is the dominant
// continuation (FROM after a projection; WHERE after a relation; ON after a
// join target). Returned labels are UPPER; downstream casing is applied by
// `applyKeywordCasing`.
//
// Pure data + one function. No parse, no I/O, safe on the resolver hot path.
//
// Design: `docs/superpowers/specs/2026-06-14-legal-next-keyword-autocomplete-design.md` §6.

import type { DatabaseEngineId } from '@/core/engines';

export type NextKeywordSlot =
  | 'after-select-projection'
  | 'after-from-relation-plain'
  | 'after-join-target'
  | 'after-where-predicate'
  | 'after-on-predicate'
  | 'after-having-predicate'
  | 'after-group-by-item'
  | 'after-order-by-item'
  | 'after-order-by-direction'
  // `EXISTS (▮` — a query is the only legal continuation.
  | 'after-exists-open'
  // Join-ladder heads: `LEFT ▮`, `LEFT OUTER ▮`, `NATURAL ▮`, `CROSS ▮`.
  | 'after-outer-join-side'
  | 'after-outer'
  | 'after-natural'
  | 'after-cross'
  // Two-word constraint heads inside DDL: `PRIMARY ▮`, `FOREIGN ▮`.
  | 'after-key-constraint-head'
  // `INSERT OR ▮` (SQLite conflict clause).
  | 'after-insert-or'
  // `… FROM t USE ▮` / `FORCE ▮` / `IGNORE ▮` (MySQL index hints).
  | 'after-index-hint-verb'
  // `… FOR JSON ▮` / `FOR XML ▮` (T-SQL result serialization).
  | 'after-for-json'
  | 'after-for-xml'
  // `DELETE FROM t ▮` — a DELETE takes none of the JOIN ladder.
  | 'after-delete-relation'
  // `CREATE TABLE t (id INT ▮` — the column's constraints.
  | 'after-column-data-type'
  // `CREATE VIEW v ▮` — the body introducer.
  | 'after-create-view-name'
  // `ALTER TABLE t ADD ▮` — what is being added.
  | 'after-alter-table-add'
  // `BEGIN ▮` — the transaction modes.
  | 'after-begin'
  // `EXPLAIN (▮` — the option list.
  | 'at-explain-option'
  // `LOCK TABLES t ▮` (MySQL) — the lock mode.
  | 'after-lock-tables-relation'
  // `SELECT … INTO ▮` (MySQL) — the destination.
  | 'after-select-into'
  // Row-limiting clause sub-positions (`FETCH …` / `OFFSET …` / `LIMIT …`).
  // Every engine's paging grammar is keyword-only here — a column or table
  // name is never legal — so these slots exist for all five engines and the
  // per-engine deltas below decide which ones actually carry keywords.
  | 'after-fetch'
  | 'after-fetch-direction'
  | 'after-fetch-count'
  | 'after-fetch-rows'
  | 'after-offset-count'
  | 'after-offset-rows'
  | 'after-limit-count'
  // The spot where the row count / offset count itself goes. An integer literal
  // is what the user types, so these lists are the clause-shape hint for what
  // follows the number, and may legitimately be empty.
  | 'at-limit-count'
  | 'at-offset-count'
  // The row-limiting clause is finished — only a locking clause / query hint
  // can follow. Set operators cannot: LIMIT/FETCH bind to the whole set
  // expression, so `… LIMIT 5 UNION …` is a syntax error in every dialect.
  | 'after-row-limit-clause'
  // Predicate operators whose continuation is a fixed keyword set: `IS ▮`,
  // `IS NOT ▮`, and the low bound of a `BETWEEN ▮ AND ▮` range.
  | 'after-is'
  | 'after-is-not'
  | 'after-between-low'
  // CASE expression structure — each region has a small fixed continuation.
  | 'after-case-selector'
  | 'after-case-when-predicate'
  | 'after-case-then-result'
  | 'after-case-else-result'
  // Statement shapes whose next token is a clause keyword, not a name.
  | 'after-update-target'
  | 'after-set-assignment'
  | 'after-insert-columns'
  | 'after-insert-values'
  | 'after-cte-body'
  | 'after-window-partition-item'
  | 'at-window-spec'
  | 'after-window-frame-unit'
  | 'after-window-order-item'
  | 'after-window-order-direction'
  // Boundaries where the first word of a multi-word construct has been typed
  // and only its completion is legal.
  | 'after-set-operator'
  | 'after-difference-operator'
  | 'after-set-operator-quantifier'
  | 'after-nulls'
  | 'after-group-by-with'
  | 'after-is-distinct'
  | 'after-not-operator'
  // MERGE's branch ladder — the clause machine has no state for it, so every
  // caret here used to answer with whatever clause the last keyword set.
  | 'after-merge-on'
  | 'after-merge-when'
  | 'after-merge-not'
  | 'after-merge-matched'
  | 'after-merge-not-matched'
  | 'after-merge-then-matched'
  | 'after-merge-then-not-matched'
  | 'after-merge-by-side'
  | 'after-merge-update'
  | 'after-merge-insert'
  | 'after-merge-branch-end'
  // Upsert tails.
  | 'after-insert-on'
  | 'after-on-conflict'
  | 'after-conflict-target'
  | 'after-conflict-do'
  | 'after-duplicate-key'
  // Statement heads and hint lists.
  | 'after-show'
  | 'at-table-hint'
  | 'at-query-hint'
  | 'at-top-count'
  // Statement heads — the first word is written and only an object kind, a
  // sub-statement, or a fixed option can follow.
  | 'after-create'
  | 'after-drop'
  | 'after-alter'
  | 'after-explain'
  | 'after-delete'
  | 'after-comment-on'
  | 'after-grant-object'
  | 'after-transaction-verb'
  | 'after-commit-verb'
  | 'after-rollback-verb'
  // Post-aggregate constructs and the named-window clause.
  | 'after-within'
  | 'at-filter-clause'
  | 'after-grouping'
  | 'at-named-window-spec'
  // CREATE TRIGGER ladder. The slot seam already CLASSIFIES these positions;
  // only sqlite had the emission, so the others floods despite knowing exactly
  // where the caret was.
  | 'at-trigger-timing'
  | 'at-trigger-event'
  // Positions with a small, engine-specific vocabulary that nothing modelled.
  | 'after-collate'
  | 'after-tablesample'
  | 'after-tsql-trigger-target'
  | 'at-extract-field'
  | 'after-alter-column-name'
  | 'at-within-group'
  | 'after-like-pattern'
  | 'after-similar'
  | 'after-interval-value'
  | 'after-at';

export type LegalKeywordOpts = {
  readonly relationHasAlias?: boolean;
  readonly projectionIsStar?: boolean;
};

type EngineId = DatabaseEngineId; // 'mssql' | 'pgsql' | 'sqlite' | 'mysql' | 'oracle' | 'mock'

// BASE ordered legal keywords per slot (ANSI). Conditional tokens (`AS`,
// `INTO`, `USING`, `NULLS`) are listed here and stripped below per engine /
// opts. `mock` falls through to BASE only (no deltas).
const BASE: Record<NextKeywordSlot, readonly string[]> = {
  'after-select-projection': ['FROM', 'AS', 'INTO'],
  'after-from-relation-plain': [
    'WHERE',
    'JOIN',
    'INNER',
    'LEFT',
    'RIGHT',
    'FULL',
    'CROSS',
    'OUTER',
    'AS',
    'GROUP',
    'ORDER',
    'HAVING',
    'UNION',
    'EXCEPT',
    'INTERSECT',
  ],
  'after-join-target': ['ON', 'USING', 'AS'],
  'after-where-predicate': ['AND', 'OR', 'GROUP', 'ORDER', 'HAVING', 'UNION', 'EXCEPT', 'INTERSECT'],
  'after-on-predicate': [
    'AND',
    'OR',
    'WHERE',
    'JOIN',
    'INNER',
    'LEFT',
    'RIGHT',
    'FULL',
    'CROSS',
    'GROUP',
    'ORDER',
    'HAVING',
    'UNION',
    'EXCEPT',
    'INTERSECT',
  ],
  'after-having-predicate': ['AND', 'OR', 'ORDER', 'UNION', 'EXCEPT', 'INTERSECT'],
  'after-group-by-item': ['HAVING', 'ORDER', 'UNION', 'EXCEPT', 'INTERSECT'],
  // `NULLS FIRST` / `NULLS LAST` are emitted as the complete two-token clause
  // (more useful than a bare `NULLS`); gated to engines that support null
  // ordering. ASC/DESC lead since they are the dominant continuation.
  'after-order-by-item': ['ASC', 'DESC', 'NULLS FIRST', 'NULLS LAST'],
  'after-exists-open': ['SELECT'],
  'after-outer-join-side': ['OUTER', 'JOIN'],
  'after-outer': ['JOIN'],
  'after-natural': ['JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL'],
  'after-cross': ['JOIN'],
  'after-key-constraint-head': ['KEY'],
  'after-delete-relation': ['WHERE'],
  // Column constraints are near-universal; the dialect-only ones (GENERATED,
  // AUTO_INCREMENT, IDENTITY, …) come from the deltas.
  'after-column-data-type': [
    'NOT NULL', 'NULL', 'PRIMARY KEY', 'UNIQUE', 'DEFAULT', 'CHECK', 'REFERENCES', 'COLLATE',
  ],
  'after-create-view-name': ['AS'],
  'after-alter-table-add': [
    'COLUMN', 'CONSTRAINT', 'PRIMARY KEY', 'UNIQUE', 'FOREIGN KEY', 'CHECK',
  ],
  // Dialect-only slots — an engine without the construct declines them.
  'after-begin': [],
  'at-explain-option': [],
  'after-lock-tables-relation': [],
  'after-select-into': [],
  // Dialect-only slots — the BASE is empty and each engine's delta supplies the
  // vocabulary, so an engine without the construct declines the slot entirely.
  'after-insert-or': [],
  'after-index-hint-verb': [],
  'after-for-json': [],
  'after-for-xml': [],
  // A direction does not end the statement — the paging clauses follow it, and
  // only the null-ordering engines add NULLS FIRST/LAST (gated below).
  'after-order-by-direction': ['NULLS FIRST', 'NULLS LAST'],
  // Row-limiting slots have NO ANSI base — the paging clause differs too much
  // between dialects (`FETCH FIRST n ROWS ONLY` vs `LIMIT n OFFSET m`), so
  // every keyword comes from the per-engine deltas. An engine that lists
  // nothing for a slot yields an empty array, and the resolvers' keyword-only
  // short-circuit declines — behavior there is unchanged.
  'after-fetch': [],
  'after-fetch-direction': [],
  'after-fetch-count': [],
  'after-fetch-rows': [],
  'after-offset-count': [],
  'after-offset-rows': [],
  'after-limit-count': [],
  'at-limit-count': [],
  'at-offset-count': [],
  'after-row-limit-clause': [],
  // `IS ▮` takes a null test or a boolean/JSON test — never an identifier. The
  // two-token `NOT NULL` is listed whole because it is the useful completion;
  // engines add their own tests (`DISTINCT FROM`, `TRUE`/`FALSE`, `JSON`).
  'after-is': ['NULL', 'NOT NULL'],
  'after-is-not': ['NULL'],
  // The low bound of a BETWEEN range is followed by exactly one keyword.
  'after-between-low': ['AND'],
  // CASE. A simple CASE's selector (`CASE x ▮`) admits only WHEN; a completed
  // WHEN predicate admits THEN plus the boolean connectives; a completed THEN
  // result admits the next branch, the default, or the terminator.
  'after-case-selector': ['WHEN'],
  'after-case-when-predicate': ['THEN', 'AND', 'OR'],
  'after-case-then-result': ['WHEN', 'ELSE', 'END'],
  'after-case-else-result': ['END'],
  // `UPDATE <target> ▮` — SET is the only clause that can follow the target.
  'after-update-target': ['SET'],
  // A completed `SET col = value` assignment. (`,` for the next assignment is
  // punctuation, not a completion.)
  'after-set-assignment': ['WHERE'],
  // `INSERT INTO t (cols) ▮` — the row source comes next.
  'after-insert-columns': ['VALUES', 'SELECT'],
  // A completed `VALUES (…)` tuple — engine-specific upsert / returning tails.
  'after-insert-values': [],
  // `WITH q AS (…) ▮` — the statement the CTE prolog belongs to.
  'after-cte-body': ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
  // `OVER (▮` — the window spec's own head. The two-word forms are emitted
  // whole because the second word is never anything else here.
  'at-window-spec': ['PARTITION BY', 'ORDER BY', 'ROWS', 'RANGE'],
  // `OVER (… ROWS ▮` / `RANGE ▮` — the frame extent.
  'after-window-frame-unit': ['BETWEEN', 'UNBOUNDED PRECEDING', 'CURRENT ROW'],
  // `OVER (PARTITION BY x ▮` — ordering or a frame comes next.
  'after-window-partition-item': ['ORDER', 'ROWS', 'RANGE'],
  // `OVER (ORDER BY x ▮` — like a statement ORDER BY, except a frame may follow
  // and the statement-level tails (LIMIT / OFFSET / FETCH) may not.
  'after-window-order-item': ['ASC', 'DESC', 'ROWS', 'RANGE', 'NULLS FIRST', 'NULLS LAST'],
  'after-window-order-direction': ['ROWS', 'RANGE', 'NULLS FIRST', 'NULLS LAST'],
  // `UNION ▮` — a new query branch starts here; nothing that follows a
  // relation is legal. `UNION ALL` is universal.
  'after-set-operator': ['SELECT', 'ALL'],
  // `EXCEPT ▮` / `INTERSECT ▮` / `MINUS ▮` — same, but the `ALL` quantifier is
  // NOT universal (T-SQL and Oracle have no `EXCEPT ALL`), so it is a delta.
  'after-difference-operator': ['SELECT'],
  'after-set-operator-quantifier': ['SELECT'],
  // The first word of a two-word construct: only its completion is legal.
  'after-nulls': ['FIRST', 'LAST'],
  // `GROUP BY x WITH ROLLUP` is MySQL / T-SQL syntax — PG, Oracle and SQLite
  // spell it `GROUP BY ROLLUP(x)`, so the base is empty.
  'after-group-by-with': [],
  'after-is-distinct': ['FROM'],
  // `x NOT ▮` — the negatable operators.
  'after-not-operator': ['IN', 'LIKE', 'BETWEEN'],
  // MERGE. `WHEN`/`MATCHED`/`THEN` are ANSI; the actions and the `BY TARGET |
  // BY SOURCE` qualifiers differ per dialect (deltas below).
  'after-merge-on': ['WHEN', 'AND', 'OR'],
  'after-merge-when': ['MATCHED', 'NOT MATCHED'],
  'after-merge-not': ['MATCHED'],
  'after-merge-matched': ['THEN', 'AND'],
  'after-merge-not-matched': ['THEN', 'AND'],
  'after-merge-then-matched': ['UPDATE SET', 'DELETE'],
  'after-merge-then-not-matched': ['INSERT'],
  // `WHEN NOT MATCHED BY SOURCE ▮` — the side is chosen, so only THEN / AND
  // follow, and its action set is the MATCHED one (INSERT is BY TARGET only).
  'after-merge-by-side': ['THEN', 'AND'],
  'after-merge-update': ['SET'],
  'after-merge-insert': ['VALUES'],
  'after-merge-branch-end': ['WHEN'],
  // Upsert tails — every keyword here is dialect-specific, so the base is
  // empty and the deltas carry it.
  'after-insert-on': [],
  'after-on-conflict': [],
  'after-conflict-target': [],
  'after-conflict-do': [],
  'after-duplicate-key': [],
  'after-show': [],
  'at-table-hint': [],
  'at-query-hint': [],
  // `SELECT TOP ▮` — an integer (or a parenthesized expression) goes here.
  'at-top-count': [],
  // Statement heads. Only an object kind follows CREATE / DROP / ALTER, and
  // only a statement follows EXPLAIN — yet these used to lead with the schema
  // flood, burying the wanted keyword 130–190 items down.
  'after-create': ['TABLE', 'VIEW', 'INDEX', 'FUNCTION', 'PROCEDURE', 'TRIGGER', 'SEQUENCE', 'SCHEMA', 'DATABASE'],
  'after-drop': ['TABLE', 'VIEW', 'INDEX', 'FUNCTION', 'PROCEDURE', 'TRIGGER', 'SEQUENCE', 'SCHEMA', 'DATABASE'],
  'after-alter': ['TABLE', 'VIEW', 'INDEX', 'SEQUENCE', 'SCHEMA', 'DATABASE'],
  'after-explain': ['SELECT', 'INSERT', 'UPDATE', 'DELETE'],
  // `DELETE ▮` — the engines that require FROM. T-SQL makes it optional (a bare
  // target is legal), so its list stays empty and the position falls through to
  // the relation emission.
  'after-delete': [],
  // `GRANT … ON <object> ▮` — the grantee clause. REVOKE spells it `FROM`;
  // both are offered because the classifier does not distinguish them and
  // either is one keystroke of filtering away.
  'after-grant-object': ['TO', 'FROM'],
  'after-comment-on': [
    'TABLE', 'COLUMN', 'VIEW', 'MATERIALIZED VIEW', 'SCHEMA', 'INDEX', 'FUNCTION',
    'TRIGGER', 'SEQUENCE', 'TYPE', 'DATABASE',
  ],
  // `START ▮` — only `TRANSACTION` follows it (the WORK / ISOLATION LEVEL
  // forms belong to `BEGIN`, which is deliberately not a statement head here).
  'after-transaction-verb': ['TRANSACTION'],
  'after-commit-verb': ['TRANSACTION'],
  // `TO SAVEPOINT` is ROLLBACK-only in every dialect.
  'after-rollback-verb': ['TRANSACTION', 'TO SAVEPOINT'],
  // `PERCENTILE_CONT(…) WITHIN ▮` and `COUNT(*) FILTER (▮` each take exactly
  // one construct.
  'after-within': ['GROUP'],
  'at-filter-clause': ['WHERE'],
  // `GROUP BY GROUPING ▮` — the grouping-sets construct.
  'after-grouping': ['SETS'],
  // `WINDOW w AS (▮` — the same body as `OVER (▮`.
  'at-named-window-spec': ['PARTITION BY', 'ORDER BY', 'ROWS', 'RANGE'],
  'at-trigger-timing': ['BEFORE', 'AFTER', 'INSTEAD OF'],
  'at-trigger-event': ['INSERT', 'UPDATE', 'DELETE'],
  // Collations and sampling methods are engine-specific vocabularies, so the
  // base is empty and the deltas carry them.
  'after-collate': [],
  'after-tablesample': [],
  // T-SQL spells its trigger ladder `CREATE TRIGGER n ON t AFTER|FOR|INSTEAD OF`
  // — a shape the PG-style recognizer does not model, so this caret had none of
  // these words anywhere in the list.
  'after-tsql-trigger-target': [],
  // `EXTRACT(▮ FROM …)` — the datetime field. ANSI defines these six; the
  // deltas add each dialect's extensions. Only PG had this before, so MySQL
  // put `YEAR` at rank 224 and Oracle at 159.
  'at-extract-field': ['YEAR', 'MONTH', 'DAY', 'HOUR', 'MINUTE', 'SECOND'],
  // `ALTER TABLE t ALTER COLUMN <name> ▮` — PostgreSQL's form, where a keyword
  // chooses the edit. (T-SQL puts a data type here; its own slot claims that.)
  'after-alter-column-name': [],
  // `WITHIN GROUP (▮` — the ordered-set aggregate's sort specification.
  'at-within-group': ['ORDER BY'],
  // `x LIKE 'a%' ▮` — the optional escape designator.
  'after-like-pattern': ['ESCAPE'],
  // `x SIMILAR ▮` — only `TO` completes the operator.
  'after-similar': ['TO'],
  // `INTERVAL '1' ▮` / `INTERVAL 1 ▮` — the unit qualifier.
  'after-interval-value': ['DAY', 'MONTH', 'YEAR', 'HOUR', 'MINUTE', 'SECOND'],
  // `<ts> AT ▮` — the time-zone conversion.
  'after-at': ['TIME ZONE'],
};

/**
 * Every slot name, in BASE's declaration order.
 *
 * For callers that need the WHOLE vocabulary rather than one caret's list —
 * `compoundKeywordRemainder.ts` scans it to learn which multi-word keywords an
 * engine spells (`CURRENT ROW`, `UNBOUNDED PRECEDING`, `ON DUPLICATE KEY
 * UPDATE`, …) so a caret attached to the second word of one can still complete
 * the compound. Derived from `BASE`, which is a total Record, so a new slot
 * joins the scan automatically.
 */
export const ALL_NEXT_KEYWORD_SLOTS: readonly NextKeywordSlot[] = Object.keys(
  BASE,
) as NextKeywordSlot[];

/**
 * Every sub-position inside (or immediately after) the row-limiting clause.
 *
 * The whole clause is a CLOSED region: from the `LIMIT` / `OFFSET` / `FETCH`
 * keyword to the end of the statement, the only legal tokens are integer
 * literals, the clause's own keywords, and integer-valued operands in scope
 * (procedural locals, routine parameters — that is how `LIMIT p_limit` inside a
 * function body completes). A table, a column, a routine or the general keyword
 * list is a syntax error at every one of these carets, so a resolver that
 * recognizes one of these slots emits this slot's keywords plus those operands
 * and stops.
 *
 * A slot with an EMPTY keyword list still suppresses — MySQL's `LIMIT ▮` takes
 * a bare integer and nothing else, and closing the widget is the right answer
 * there. That is why membership in this set, not the emptiness of the list,
 * drives the short-circuit.
 *
 * Every keyword here is legal AT THE CARET. An earlier revision hinted the
 * token that comes after the number (`ROWS` at `OFFSET ▮`), which reads as
 * helpful but is a landmine: Monaco highlights the first item and Enter accepts
 * it, so a user reaching for a newline got `OFFSET ROWS`. Nothing is lost by
 * dropping it — the grammar delivers the same keyword one keystroke later, at
 * `after-offset-count`, the moment it becomes legal. `LIMIT ALL` (PG) and the
 * countless `FETCH FIRST ROWS ONLY` form (PG/Oracle) stay because they ARE
 * legal at their caret; T-SQL requires the count, so its `after-fetch-direction`
 * is empty and simply suppresses.
 */
export const ROW_LIMIT_SLOTS: ReadonlySet<NextKeywordSlot> = new Set<NextKeywordSlot>([
  'after-fetch',
  'after-fetch-direction',
  'after-fetch-count',
  'after-fetch-rows',
  'after-offset-count',
  'after-offset-rows',
  'after-limit-count',
  'at-limit-count',
  'at-offset-count',
  'at-top-count',
  'after-row-limit-clause',
]);

/** True when the slot sits in the row-limiting clause (see the set above). */
export function isRowLimitSlot(slot: NextKeywordSlot): boolean {
  return ROW_LIMIT_SLOTS.has(slot);
}

// Per-engine deltas appended after the BASE tail (popularity order). `NATURAL`
// is placed in the delta list near the JOIN family; exact intra-list position
// is tunable later — `WHERE`/`ON`/the dominant token stays first via BASE.
const DELTAS: Partial<Record<EngineId, Partial<Record<NextKeywordSlot, readonly string[]>>>> = {
  mssql: {
    'after-column-data-type': ['IDENTITY', 'SPARSE', 'ROWGUIDCOL', 'MASKED WITH'],
    'after-create-view-name': ['WITH'],
    'after-delete-relation': ['FROM', 'OUTPUT', 'WHERE'],
    'after-cross': ['APPLY'],
    'after-for-json': ['AUTO', 'PATH'],
    'after-for-xml': ['RAW', 'AUTO', 'EXPLICIT', 'PATH'],
    'after-from-relation-plain': ['FOR', 'OPTION', 'PIVOT', 'UNPIVOT'],
    'after-where-predicate': ['OFFSET', 'FOR', 'OPTION'],
    'after-having-predicate': ['OFFSET', 'FOR', 'OPTION'],
    'after-order-by-item': ['OFFSET', 'FOR', 'OPTION'],
    'after-order-by-direction': ['OFFSET', 'FOR', 'OPTION'],
    // T-SQL paging: `OFFSET n ROWS FETCH { FIRST | NEXT } n ROWS ONLY`. The
    // row count after FIRST/NEXT is MANDATORY (unlike PG/Oracle, where it is
    // optional), so `after-fetch-direction` has NO legal keyword — it stays
    // empty and the slot suppresses. `WITH TIES` is TOP-only syntax in T-SQL,
    // never part of FETCH.
    'after-fetch': ['NEXT', 'FIRST'],
    'after-row-limit-clause': ['OPTION', 'FOR XML', 'FOR JSON'],
    // T-SQL's `GROUP BY … WITH ROLLUP | CUBE`.
    'after-group-by-with': ['ROLLUP', 'CUBE'],
    'after-create': ['OR ALTER', 'UNIQUE INDEX', 'CLUSTERED INDEX', 'TYPE', 'LOGIN', 'USER', 'ROLE', 'SYNONYM'],
    'after-drop': ['TYPE', 'LOGIN', 'USER', 'ROLE', 'SYNONYM'],
    'after-alter': ['FUNCTION', 'PROCEDURE', 'TRIGGER', 'ROLE', 'USER'],
    // T-SQL has no BEFORE trigger, and spells the AFTER form `FOR` as well.
    'at-trigger-timing': ['FOR'],
    // T-SQL spells a multi-event trigger as a COMMA-separated list
    // (`AFTER INSERT, UPDATE`) where PG / Oracle write `INSERT OR UPDATE`, so
    // the combinations are usable labels here — the base slot's three single
    // verbs cannot express them. `AFTER` / `INSTEAD OF` ride along because
    // T-SQL puts the timing keyword at this same caret (after the target
    // table), so a user at `AFTER ▮` may still be choosing the timing.
    // Closes mssql script.36 §1 + script.55 §1.
    'at-trigger-event': [
      'INSERT, UPDATE', 'INSERT, DELETE', 'UPDATE, DELETE', 'INSERT, UPDATE, DELETE',
      'AFTER', 'INSTEAD OF',
    ],
    'after-tsql-trigger-target': ['AFTER', 'FOR', 'INSTEAD OF', 'WITH'],
    'after-tablesample': ['SYSTEM'],
    'after-collate': [
      'SQL_Latin1_General_CP1_CI_AS', 'SQL_Latin1_General_CP1_CS_AS',
      'Latin1_General_100_CI_AS_SC_UTF8', 'DATABASE_DEFAULT',
    ],
    // T-SQL alone can restrict a non-matching MERGE branch to one side.
    'after-merge-not-matched': ['BY TARGET', 'BY SOURCE'],
    // A matched branch may also delete; a completed action may be followed by
    // the next branch or the statement's OUTPUT / OPTION tails.
    'after-merge-branch-end': ['OUTPUT', 'OPTION'],
    // Table and query hints — the paren contents are a fixed vocabulary.
    'at-table-hint': [
      'NOLOCK', 'READUNCOMMITTED', 'READCOMMITTED', 'REPEATABLEREAD', 'SERIALIZABLE',
      'READPAST', 'ROWLOCK', 'PAGLOCK', 'TABLOCK', 'TABLOCKX', 'UPDLOCK', 'XLOCK',
      'HOLDLOCK', 'NOEXPAND', 'FORCESEEK', 'FORCESCAN', 'INDEX',
    ],
    'at-query-hint': [
      'RECOMPILE', 'MAXDOP', 'MAXRECURSION', 'OPTIMIZE FOR', 'OPTIMIZE FOR UNKNOWN',
      'FAST', 'HASH JOIN', 'LOOP JOIN', 'MERGE JOIN', 'KEEPFIXED PLAN', 'KEEP PLAN',
      'USE HINT',
    ],
    // T-SQL: `UPDATE t SET … FROM … OUTPUT … WHERE …`; no RETURNING, and the
    // upsert tail is MERGE, not an INSERT suffix.
    'after-set-assignment': ['FROM', 'OUTPUT', 'OPTION'],
    'after-insert-columns': ['OUTPUT', 'DEFAULT VALUES'],
    'after-cte-body': ['MERGE'],
    'after-fetch-count': ['ROWS', 'ROW'],
    'after-fetch-rows': ['ONLY'],
    'after-offset-count': ['ROWS', 'ROW'],
    'after-offset-rows': ['FETCH'],
  },
  pgsql: {
    'after-column-data-type': ['GENERATED', 'DEFERRABLE'],
    'after-create-view-name': ['WITH'],
    'after-alter-table-add': ['EXCLUDE'],
    'after-begin': ['ISOLATION LEVEL', 'READ WRITE', 'READ ONLY', 'DEFERRABLE', 'NOT DEFERRABLE', 'TRANSACTION'],
    'at-explain-option': ['ANALYZE', 'VERBOSE', 'COSTS', 'SETTINGS', 'BUFFERS', 'WAL', 'TIMING', 'SUMMARY', 'FORMAT'],
    'after-delete-relation': ['USING', 'WHERE', 'RETURNING', 'AS'],
    // A CTE prolog may lead the subquery inside EXISTS; `VALUES` and `TABLE`
    // are complete queries in their own right.
    'after-exists-open': ['WITH', 'VALUES', 'TABLE'],
    'after-from-relation-plain': ['NATURAL', 'LIMIT', 'OFFSET', 'FETCH'],
    'after-on-predicate': ['NATURAL'],
    'after-join-target': ['NATURAL'],
    'after-where-predicate': ['LIMIT', 'OFFSET', 'FETCH'],
    'after-having-predicate': ['LIMIT', 'OFFSET', 'FETCH'],
    'after-group-by-item': ['LIMIT', 'OFFSET', 'FETCH'],
    'after-order-by-item': ['LIMIT', 'OFFSET', 'FETCH'],
    'after-order-by-direction': ['LIMIT', 'OFFSET', 'FETCH'],
    // `FETCH { FIRST | NEXT } [ n ] { ROW | ROWS } { ONLY | WITH TIES }` —
    // the count is optional (defaults to 1) and PERCENT is not PG syntax.
    'after-fetch': ['FIRST', 'NEXT'],
    'after-fetch-direction': ['ROWS', 'ROW'],
    'after-fetch-count': ['ROWS', 'ROW'],
    'after-fetch-rows': ['ONLY', 'WITH TIES'],
    // PG accepts the paging clauses in either order (`OFFSET 5 LIMIT 10`), and
    // a locking clause may close the statement.
    'after-offset-count': ['ROWS', 'ROW', 'LIMIT', 'FETCH', 'FOR UPDATE', 'FOR SHARE'],
    'after-offset-rows': ['FETCH'],
    // `LIMIT ALL` is PG's explicit "no limit" form, so the count slot has one
    // real keyword of its own. `OFFSET ▮` has none — only a count goes there.
    'at-limit-count': ['ALL'],
    'after-limit-count': ['OFFSET', 'FETCH', 'FOR UPDATE', 'FOR SHARE'],
    'after-set-operator': ['DISTINCT', 'VALUES', 'TABLE'],
    'after-row-limit-clause': ['FOR UPDATE', 'FOR SHARE'],
    'after-is': ['NULL', 'NOT NULL', 'TRUE', 'FALSE', 'DISTINCT FROM'],
    'after-is-not': ['NULL', 'TRUE', 'FALSE', 'DISTINCT FROM'],
    'after-set-assignment': ['FROM', 'RETURNING'],
    'after-insert-columns': ['OVERRIDING', 'DEFAULT VALUES'],
    'after-insert-values': ['ON CONFLICT', 'RETURNING'],
    'at-window-spec': ['GROUPS'],
    'after-window-partition-item': ['GROUPS'],
    'after-window-order-item': ['GROUPS'],
    'after-window-order-direction': ['GROUPS'],
    'after-cte-body': ['MERGE'],
    'after-difference-operator': ['ALL'],
    'after-not-operator': ['ILIKE', 'SIMILAR TO'],
    'after-create': [
      'OR REPLACE', 'MATERIALIZED VIEW', 'UNIQUE INDEX', 'TYPE', 'EXTENSION', 'ROLE',
      'POLICY', 'DOMAIN', 'TEMP TABLE', 'TABLE IF NOT EXISTS',
    ],
    'after-drop': ['MATERIALIZED VIEW', 'TYPE', 'EXTENSION', 'ROLE', 'POLICY', 'DOMAIN'],
    'after-alter': ['MATERIALIZED VIEW', 'TYPE', 'ROLE', 'POLICY', 'DOMAIN', 'FUNCTION', 'PROCEDURE'],
    'after-explain': ['ANALYZE', 'VERBOSE', '(FORMAT JSON)'],
    'after-merge-then-matched': ['DO NOTHING'],
    'after-tablesample': ['BERNOULLI', 'SYSTEM'],
    'after-alter-column-name': [
      'TYPE', 'SET DATA TYPE', 'SET DEFAULT', 'DROP DEFAULT', 'SET NOT NULL',
      'DROP NOT NULL',
    ],
    'after-interval-value': ['WEEK', 'DECADE', 'CENTURY', 'MILLENNIUM'],
    'at-extract-field': [
      'DOW', 'DOY', 'EPOCH', 'WEEK', 'QUARTER', 'MILLENNIUM', 'CENTURY', 'DECADE',
      'MICROSECONDS', 'MILLISECONDS', 'TIMEZONE', 'TIMEZONE_HOUR', 'TIMEZONE_MINUTE',
      'ISOYEAR', 'ISODOW', 'JULIAN',
    ],
    'after-collate': ['"C"', '"POSIX"', '"default"', '"ucs_basic"'],
    'after-merge-then-not-matched': ['DO NOTHING'],
    'after-delete': ['FROM'],
    'after-commit-verb': ['WORK'],
    'after-rollback-verb': ['WORK'],
    'at-trigger-event': ['TRUNCATE'],
    'at-named-window-spec': ['GROUPS'],
    // PG's upsert: `ON CONFLICT [ (cols) | ON CONSTRAINT c ] DO { NOTHING |
    // UPDATE SET … }`.
    'after-insert-on': ['CONFLICT'],
    'after-on-conflict': ['DO NOTHING', 'DO UPDATE SET', 'ON CONSTRAINT'],
    'after-conflict-target': ['DO UPDATE SET', 'DO NOTHING', 'WHERE'],
    'after-conflict-do': ['NOTHING', 'UPDATE SET'],
  },
  mysql: {
    'after-column-data-type': ['AUTO_INCREMENT', 'COMMENT', 'GENERATED ALWAYS AS', 'CHARACTER SET', 'ON UPDATE'],
    'after-alter-table-add': ['INDEX', 'KEY', 'FULLTEXT', 'SPATIAL'],
    'after-lock-tables-relation': ['READ', 'WRITE', 'LOW_PRIORITY WRITE', 'READ LOCAL', 'AS'],
    'after-select-into': ['OUTFILE', 'DUMPFILE'],
    'after-delete-relation': ['USING', 'WHERE', 'ORDER BY', 'LIMIT'],
    'after-insert-or': ['ROLLBACK', 'ABORT', 'REPLACE', 'FAIL', 'IGNORE'],
    'after-index-hint-verb': ['INDEX', 'KEY'],
    // A CTE prolog may lead the subquery inside EXISTS; `VALUES` and `TABLE`
    // are complete queries of their own since 8.0.19.
    'after-exists-open': ['WITH', 'VALUES', 'TABLE'],
    // `USE`/`FORCE`/`IGNORE` precede an `INDEX (...)` / `KEY (...)` hint that
    // legally follows a table reference in the FROM/JOIN list.
    'after-from-relation-plain': ['NATURAL', 'STRAIGHT_JOIN', 'USE', 'FORCE', 'IGNORE', 'LIMIT', 'OFFSET'],
    'after-on-predicate': ['NATURAL', 'STRAIGHT_JOIN'],
    'after-join-target': ['NATURAL', 'STRAIGHT_JOIN'],
    'after-where-predicate': ['LIMIT', 'OFFSET'],
    'after-having-predicate': ['LIMIT', 'OFFSET'],
    'after-group-by-item': ['LIMIT', 'OFFSET'],
    'after-order-by-item': ['LIMIT', 'OFFSET'],
    'after-order-by-direction': ['LIMIT', 'OFFSET'],
    // MySQL has no FETCH clause; `LIMIT n OFFSET m` is the only paging form, so
    // the position after the offset count IS the statement tail.
    'after-limit-count': ['OFFSET'],
    'after-offset-count': ['FOR UPDATE', 'FOR SHARE', 'LOCK IN SHARE MODE'],
    'after-update-target': ['JOIN'],
    'after-is': ['NULL', 'NOT NULL', 'TRUE', 'FALSE', 'UNKNOWN'],
    'after-is-not': ['NULL', 'TRUE', 'FALSE', 'UNKNOWN'],
    // MySQL has no RETURNING; ORDER BY / LIMIT are legal on a single-table
    // UPDATE, and the upsert tail is ON DUPLICATE KEY UPDATE.
    'after-set-assignment': ['ORDER', 'LIMIT'],
    'after-set-operator': ['DISTINCT'],
    'after-difference-operator': ['ALL'],
    'after-group-by-with': ['ROLLUP'],
    'after-not-operator': ['REGEXP', 'RLIKE'],
    'after-commit-verb': ['WORK'],
    'after-rollback-verb': ['WORK'],
    'after-create': ['OR REPLACE', 'UNIQUE INDEX', 'FULLTEXT INDEX', 'EVENT', 'USER', 'ROLE'],
    'after-drop': ['EVENT', 'USER', 'ROLE'],
    'after-alter': ['EVENT', 'USER'],
    'after-explain': ['ANALYZE', 'FORMAT=JSON'],
    // MySQL's upsert spelling.
    'after-insert-on': ['DUPLICATE KEY UPDATE'],
    'at-extract-field': [
      'MICROSECOND', 'WEEK', 'QUARTER', 'YEAR_MONTH', 'DAY_HOUR', 'DAY_MINUTE',
      'DAY_SECOND', 'HOUR_MINUTE', 'HOUR_SECOND', 'MINUTE_SECOND',
    ],
    'after-interval-value': [
      'MICROSECOND', 'WEEK', 'QUARTER', 'YEAR_MONTH', 'DAY_HOUR', 'DAY_MINUTE',
      'DAY_SECOND', 'HOUR_MINUTE', 'HOUR_SECOND', 'MINUTE_SECOND',
    ],
    'after-collate': [
      'utf8mb4_general_ci', 'utf8mb4_unicode_ci', 'utf8mb4_0900_ai_ci', 'utf8mb4_bin',
      'latin1_swedish_ci',
    ],
    'after-duplicate-key': ['UPDATE'],
    'after-show': [
      'TABLES', 'DATABASES', 'COLUMNS', 'CREATE TABLE', 'CREATE DATABASE', 'INDEX',
      'STATUS', 'VARIABLES', 'PROCESSLIST', 'GRANTS', 'ENGINES', 'TRIGGERS',
      'PROCEDURE STATUS', 'FUNCTION STATUS', 'WARNINGS', 'ERRORS', 'CHARACTER SET',
      'COLLATION', 'FULL TABLES', 'FULL COLUMNS', 'FULL PROCESSLIST',
    ],
    'after-insert-values': ['ON DUPLICATE KEY UPDATE'],
  },
  oracle: {
    'after-column-data-type': ['GENERATED ALWAYS AS', 'VISIBLE', 'INVISIBLE'],
    // Subquery factoring is allowed inside the EXISTS subquery.
    'after-exists-open': ['WITH'],
    'after-delete-relation': ['WHERE', 'RETURNING'],
    'after-cross': ['APPLY'],
    'after-from-relation-plain': ['NATURAL', 'START', 'CONNECT', 'MODEL', 'PIVOT', 'UNPIVOT'],
    'after-on-predicate': ['NATURAL'],
    'after-join-target': ['NATURAL'],
    'after-where-predicate': ['FETCH', 'OFFSET'],
    'after-having-predicate': ['FETCH', 'OFFSET'],
    'after-group-by-item': ['FETCH', 'OFFSET'],
    'after-order-by-item': ['FETCH', 'OFFSET'],
    'after-order-by-direction': ['FETCH', 'OFFSET'],
    // Row-limiting clause (12.1+): `OFFSET n { ROW | ROWS }` then
    // `FETCH { FIRST | NEXT } [ n [ PERCENT ] ] { ROW | ROWS }
    //  { ONLY | WITH TIES }`. The count is optional, PERCENT only follows one.
    'after-fetch': ['FIRST', 'NEXT'],
    'after-fetch-direction': ['ROWS', 'ROW'],
    'after-fetch-count': ['ROWS', 'ROW', 'PERCENT'],
    'after-fetch-rows': ['ONLY', 'WITH TIES'],
    'after-offset-count': ['ROWS', 'ROW'],
    'after-offset-rows': ['FETCH'],
    'after-row-limit-clause': ['FOR UPDATE'],
    // Oracle's `IS [NOT] JSON` check constraint / predicate.
    'after-is': ['NULL', 'NOT NULL', 'JSON', 'NOT JSON'],
    'after-is-not': ['NULL', 'JSON'],
    'after-set-assignment': ['RETURNING'],
    'after-insert-values': ['RETURNING'],
    'after-commit-verb': ['WORK'],
    'after-rollback-verb': ['WORK'],
    'after-collate': ['BINARY', 'BINARY_CI', 'BINARY_AI', 'USING_NLS_COMP'],
    // Oracle's `AT LOCAL` needs no zone argument.
    'after-at': ['LOCAL'],
    'after-interval-value': ['DAY TO SECOND', 'YEAR TO MONTH'],
    'at-extract-field': ['TIMEZONE_HOUR', 'TIMEZONE_MINUTE', 'TIMEZONE_REGION', 'TIMEZONE_ABBR'],
    'after-create': ['OR REPLACE', 'MATERIALIZED VIEW', 'UNIQUE INDEX', 'TYPE', 'PACKAGE', 'SYNONYM', 'USER', 'ROLE'],
    'after-drop': ['MATERIALIZED VIEW', 'TYPE', 'PACKAGE', 'SYNONYM', 'USER', 'ROLE'],
    'after-alter': ['MATERIALIZED VIEW', 'TYPE', 'PACKAGE', 'USER', 'SESSION', 'SYSTEM'],
    'after-explain': ['PLAN FOR'],
  },
  sqlite: {
    'after-column-data-type': ['AUTOINCREMENT', 'GENERATED ALWAYS AS', 'ON CONFLICT'],
    'after-begin': ['DEFERRED', 'IMMEDIATE', 'EXCLUSIVE', 'TRANSACTION'],
    'after-delete-relation': ['WHERE', 'RETURNING'],
    'after-insert-or': ['ROLLBACK', 'ABORT', 'REPLACE', 'FAIL', 'IGNORE'],
    // A CTE prolog may lead the subquery inside EXISTS.
    'after-exists-open': ['WITH', 'VALUES'],
    'after-from-relation-plain': ['NATURAL', 'LIMIT', 'OFFSET'],
    'after-on-predicate': ['NATURAL'],
    'after-join-target': ['NATURAL'],
    'after-where-predicate': ['LIMIT', 'OFFSET'],
    'after-having-predicate': ['LIMIT', 'OFFSET'],
    'after-group-by-item': ['LIMIT', 'OFFSET'],
    'after-order-by-item': ['LIMIT', 'OFFSET'],
    'after-order-by-direction': ['LIMIT', 'OFFSET'],
    // SQLite has no FETCH clause; `LIMIT n OFFSET m` closes the statement.
    'after-limit-count': ['OFFSET'],
    'after-is': ['NULL', 'NOT NULL', 'TRUE', 'FALSE', 'DISTINCT FROM'],
    'after-is-not': ['NULL', 'TRUE', 'FALSE', 'DISTINCT FROM'],
    'after-set-assignment': ['FROM', 'RETURNING'],
    'after-insert-columns': ['DEFAULT VALUES'],
    'after-insert-values': ['ON CONFLICT', 'RETURNING'],
    'at-window-spec': ['GROUPS'],
    'after-window-partition-item': ['GROUPS'],
    'after-window-order-item': ['GROUPS'],
    'after-window-order-direction': ['GROUPS'],
    'after-not-operator': ['GLOB', 'REGEXP', 'MATCH'],
    'after-create': ['UNIQUE INDEX', 'TEMP TABLE', 'VIRTUAL TABLE', 'TABLE IF NOT EXISTS'],
    'after-delete': ['FROM'],
    'after-explain': ['QUERY PLAN'],
    'at-named-window-spec': ['GROUPS'],
    // SQLite mirrors PG's upsert syntax.
    'after-insert-on': ['CONFLICT'],
    'after-on-conflict': ['DO NOTHING', 'DO UPDATE SET'],
    'after-collate': ['BINARY', 'NOCASE', 'RTRIM'],
    'after-conflict-target': ['DO UPDATE SET', 'DO NOTHING', 'WHERE'],
    'after-conflict-do': ['NOTHING', 'UPDATE SET'],
  },
};

const USING_ENGINES: ReadonlySet<EngineId> = new Set(['pgsql', 'mysql', 'oracle', 'sqlite']);
/** Engines whose CTE prolog can feed a DML statement. Oracle's subquery
 *  factoring is SELECT-only; MySQL 8.0 allows UPDATE / DELETE but not INSERT. */
const CTE_INSERT_ENGINES: ReadonlySet<EngineId> = new Set(['mssql', 'pgsql', 'sqlite']);
const CTE_DML_ENGINES: ReadonlySet<EngineId> = new Set(['mssql', 'pgsql', 'sqlite', 'mysql']);
/** Object kinds the CREATE / DROP / ALTER heads may name, per engine — the
 *  BASE list is the common set and these strip what a dialect lacks (SQLite has
 *  no stored routines or sequences; MySQL has no sequences; Oracle's CREATE
 *  DATABASE is a DBA operation, not something a query editor completes). */
const OBJECT_KIND_ENGINES: ReadonlyMap<string, ReadonlySet<EngineId>> = new Map([
  ['PROCEDURE', new Set<EngineId>(['mssql', 'pgsql', 'mysql', 'oracle'])],
  ['FUNCTION', new Set<EngineId>(['mssql', 'pgsql', 'mysql', 'oracle'])],
  ['SEQUENCE', new Set<EngineId>(['mssql', 'pgsql', 'oracle'])],
  ['SCHEMA', new Set<EngineId>(['mssql', 'pgsql', 'mysql', 'oracle'])],
  ['DATABASE', new Set<EngineId>(['mssql', 'pgsql', 'mysql'])],
]);
const OBJECT_KIND_SLOTS: ReadonlySet<string> = new Set(['after-create', 'after-drop', 'after-alter']);

/** Oracle's MERGE has no standalone DELETE action — the delete rides on the
 *  matched UPDATE (`UPDATE SET … DELETE WHERE …`). */
const MERGE_DELETE_ENGINES: ReadonlySet<EngineId> = new Set(['mssql', 'pgsql']);

/** Constructs that exist in some dialects only, keyed by the slot whose BASE
 *  carries them. BASE entries cannot be removed by a delta (deltas append), so
 *  every "this dialect does not have it" is an explicit gate. */
const SLOT_ENGINES: ReadonlyMap<string, ReadonlySet<EngineId>> = new Map([
  // T-SQL has no EXPLAIN (it uses SET SHOWPLAN / execution plans).
  ['after-explain', new Set<EngineId>(['pgsql', 'mysql', 'oracle', 'sqlite'])],
  // T-SQL has no NATURAL JOIN.
  ['after-natural', new Set<EngineId>(['pgsql', 'mysql', 'oracle', 'sqlite'])],
  // `INSERT OR <conflict>` is SQLite's spelling.
  ['after-insert-or', new Set<EngineId>(['sqlite'])],
  // Index hints (`USE INDEX (…)`) are MySQL's.
  ['after-index-hint-verb', new Set<EngineId>(['mysql'])],
  // `FOR JSON` / `FOR XML` are T-SQL result serializers.
  ['after-for-json', new Set<EngineId>(['mssql'])],
  ['after-for-xml', new Set<EngineId>(['mssql'])],
  // `BEGIN` starts a transaction in PG and SQLite; in T-SQL it needs
  // `TRANSACTION`/`TRAN`, and in MySQL/Oracle `BEGIN` opens a procedural block.
  ['after-begin', new Set<EngineId>(['pgsql', 'sqlite'])],
  // Parenthesised EXPLAIN options are PostgreSQL's.
  ['at-explain-option', new Set<EngineId>(['pgsql'])],
  // `LOCK TABLES` and `SELECT … INTO OUTFILE` are MySQL's.
  ['after-lock-tables-relation', new Set<EngineId>(['mysql'])],
  ['after-select-into', new Set<EngineId>(['mysql'])],
  // T-SQL and SQLite have no EXTRACT function (DATEPART / strftime instead).
  ['at-extract-field', new Set<EngineId>(['pgsql', 'mysql', 'oracle'])],
  // `SIMILAR TO` is a PostgreSQL operator.
  ['after-similar', new Set<EngineId>(['pgsql'])],
  // `AT TIME ZONE` — not MySQL or SQLite.
  ['after-at', new Set<EngineId>(['pgsql', 'oracle', 'mssql'])],
  // `WITHIN GROUP` — the ordered-set aggregates' dialects.
  ['at-within-group', new Set<EngineId>(['pgsql', 'oracle', 'mssql'])],
  // T-SQL has no INTERVAL literal.
  ['after-interval-value', new Set<EngineId>(['pgsql', 'mysql', 'oracle'])],
  // `START TRANSACTION` is PG / MySQL syntax; T-SQL and Oracle spell it
  // differently and SQLite uses BEGIN.
  ['after-transaction-verb', new Set<EngineId>(['pgsql', 'mysql'])],
  // Ordered-set aggregates (`WITHIN GROUP`) — not MySQL, not SQLite.
  ['after-within', new Set<EngineId>(['pgsql', 'oracle', 'mssql'])],
  // Aggregate `FILTER (WHERE …)` — PG and SQLite only.
  ['at-filter-clause', new Set<EngineId>(['pgsql', 'sqlite'])],
  // `GROUPING SETS` — not MySQL (it spells it `WITH ROLLUP`), not SQLite.
  ['after-grouping', new Set<EngineId>(['pgsql', 'oracle', 'mssql'])],
  // `COMMENT ON` is a statement in PG and Oracle only; T-SQL uses extended
  // properties, MySQL an inline COMMENT, SQLite has none.
  ['after-comment-on', new Set<EngineId>(['pgsql', 'oracle'])],
  // The named-window clause.
  ['at-named-window-spec', new Set<EngineId>(['pgsql', 'mysql', 'sqlite', 'oracle'])],
]);

/** Object kinds a standalone `ALTER` can name. SQLite alters only tables;
 *  MySQL has no standalone `ALTER INDEX` (it lives under ALTER TABLE). */
const ALTER_KIND_ENGINES: ReadonlyMap<string, ReadonlySet<EngineId>> = new Map([
  ['VIEW', new Set<EngineId>(['mssql', 'pgsql', 'mysql', 'oracle'])],
  ['INDEX', new Set<EngineId>(['mssql', 'pgsql', 'oracle'])],
  ['TABLE', new Set<EngineId>(['mssql', 'pgsql', 'mysql', 'oracle', 'sqlite'])],
]);

/** Oracle's `COMMENT ON` names only a table, a column or a materialized view. */
const ORACLE_COMMENT_KINDS: ReadonlySet<string> = new Set(['TABLE', 'COLUMN', 'MATERIALIZED VIEW']);

/** Oracle does not accept `AS` before a table alias, and only T-SQL starts a
 *  join family with a bare `OUTER` (its `OUTER APPLY`). */
const RELATION_AS_ENGINES: ReadonlySet<EngineId> = new Set(['mssql', 'pgsql', 'mysql', 'sqlite']);

/** T-SQL has no `BEFORE` trigger; MySQL has no `INSTEAD OF` one. */
const BEFORE_TRIGGER_ENGINES: ReadonlySet<EngineId> = new Set(['pgsql', 'mysql', 'oracle', 'sqlite']);
const INSTEAD_OF_TRIGGER_ENGINES: ReadonlySet<EngineId> = new Set(['pgsql', 'mssql', 'oracle', 'sqlite']);
const NULLS_ENGINES: ReadonlySet<EngineId> = new Set(['pgsql', 'oracle', 'sqlite']);
const INTO_ENGINES: ReadonlySet<EngineId> = new Set(['mssql', 'pgsql', 'mysql']);

/**
 * Returns the ordered list of legal continuation keywords at a recognized
 * cursor sub-position for the given engine. Array order is the popularity
 * order (index 0 = dominant continuation). Frozen — callers must not mutate.
 */
export function getLegalNextKeywords(
  engine: EngineId,
  slot: NextKeywordSlot,
  opts: LegalKeywordOpts = {},
): readonly string[] {
  const merged = [...BASE[slot], ...(DELTAS[engine]?.[slot] ?? [])];

  const result: string[] = [];
  const seen = new Set<string>();
  for (const kw of merged) {
    // Conditional / engine-gated tokens.
    if (kw === 'USING' && !USING_ENGINES.has(engine)) continue;
    if ((kw === 'NULLS FIRST' || kw === 'NULLS LAST') && !NULLS_ENGINES.has(engine)) continue;
    if (slot === 'after-nulls' && !NULLS_ENGINES.has(engine)) continue;
    const slotEngines = SLOT_ENGINES.get(slot);
    if (slotEngines && !slotEngines.has(engine)) continue;
    if (slot === 'after-comment-on' && engine === 'oracle' && !ORACLE_COMMENT_KINDS.has(kw)) {
      continue;
    }
    // Oracle's EXPLAIN is `EXPLAIN PLAN FOR <stmt>` — the bare statement verbs
    // are a syntax error and would outrank the one legal continuation.
    if (slot === 'after-explain' && engine === 'oracle' && kw !== 'PLAN FOR') continue;
    // Oracle and MySQL spell the transaction-control noise word `WORK`; only
    // T-SQL and PG accept `TRANSACTION` there.
    if (
      kw === 'TRANSACTION' &&
      (slot === 'after-commit-verb' || slot === 'after-rollback-verb') &&
      (engine === 'oracle' || engine === 'mysql')
    ) {
      continue;
    }
    // Oracle has no ALTER / DROP SCHEMA (only CREATE SCHEMA AUTHORIZATION).
    if (kw === 'SCHEMA' && engine === 'oracle' && (slot === 'after-alter' || slot === 'after-drop')) {
      continue;
    }
    // Oracle's MERGE has no branch-level `AND` — the filter goes in the
    // action's own WHERE.
    if (kw === 'AND' && engine === 'oracle' && slot.startsWith('after-merge-')) continue;
    if (slot === 'after-alter') {
      const alterEngines = ALTER_KIND_ENGINES.get(kw);
      if (alterEngines && !alterEngines.has(engine)) continue;
    }
    if (slot === 'after-from-relation-plain') {
      if (kw === 'AS' && !RELATION_AS_ENGINES.has(engine)) continue;
      // `OUTER` alone starts nothing except T-SQL's `OUTER APPLY`; elsewhere it
      // must follow LEFT / RIGHT / FULL.
      if (kw === 'OUTER' && engine !== 'mssql') continue;
    }
    if (OBJECT_KIND_SLOTS.has(slot)) {
      const engines = OBJECT_KIND_ENGINES.get(kw);
      if (engines && !engines.has(engine)) continue;
    }
    if (kw === 'DELETE' && slot === 'after-merge-then-matched' && !MERGE_DELETE_ENGINES.has(engine)) {
      continue;
    }
    if (slot === 'at-trigger-timing') {
      if (kw === 'BEFORE' && !BEFORE_TRIGGER_ENGINES.has(engine)) continue;
      if (kw === 'INSTEAD OF' && !INSTEAD_OF_TRIGGER_ENGINES.has(engine)) continue;
    }
    if (kw === 'INTO' && !INTO_ENGINES.has(engine)) continue;
    if (slot === 'after-cte-body') {
      if (kw === 'INSERT' && !CTE_INSERT_ENGINES.has(engine)) continue;
      if ((kw === 'UPDATE' || kw === 'DELETE') && !CTE_DML_ENGINES.has(engine)) continue;
    }
    // `AS` at select-projection: star has no column alias.
    if (kw === 'AS' && slot === 'after-select-projection' && opts.projectionIsStar === true) continue;
    // `AS` at a relation/join slot: alias already bound.
    if (
      kw === 'AS' &&
      (slot === 'after-from-relation-plain' || slot === 'after-join-target') &&
      opts.relationHasAlias === true
    ) {
      continue;
    }
    // De-dup preserving first occurrence (a delta may re-list a base token).
    if (seen.has(kw)) continue;
    seen.add(kw);
    result.push(kw);
  }

  return Object.freeze(result);
}
